import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { MuiTelInput } from 'mui-tel-input';

import Button from '@/components/Button';
import InputField from '@/components/InputField';

import useNewUsers from './useNewUsers';

interface NewUsersProps {
  onSuccess?: () => void;
}

const NewUsers = ({ onSuccess }: NewUsersProps) => {
  const theme = useTheme();
  const hook = useNewUsers(onSuccess);

  return (
    <Box component="form" onSubmit={hook.onSubmit} sx={{ mt: 2 }}>
      <Grid container spacing={3} width={1}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="h6" color="primary" gutterBottom>
            Crear nuevo usuario
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Completa todos los campos para crear una nueva cuenta de usuario
          </Typography>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle2" color="text.primary" gutterBottom>
            Información de la Organización
          </Typography>
          <FormControl
            fullWidth
            error={Boolean(hook.errors.organizationCode)}
            disabled={hook.loadingOrganizations}
          >
            <InputLabel>Código de Organización *</InputLabel>
            <Select
              label="Código de Organización *"
              {...hook.register('organizationCode')}
              startAdornment={<BusinessOutlinedIcon sx={{ mr: 1, color: 'text.secondary' }} />}
            >
              {hook.organizations.map((org) => (
                <MenuItem key={org.organizationCode} value={org.organizationCode}>
                  <Box>
                    <Typography variant="body1">{org.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {org.type === 'principal' ? 'Principal' : 'Secundaria'}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            {hook.errors.organizationCode && (
              <FormHelperText>{hook.errors.organizationCode.message}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle2" color="text.primary" gutterBottom>
            Información del Usuario
          </Typography>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <InputField
            label="Correo Electrónico"
            name="user"
            errors={hook.errors}
            register={hook.register}
            sxInput={{ mt: 0 }}
            sxLabel={{ color: theme.palette.secondary.main, fontSize: '1rem' }}
            icon={<PersonOutlineOutlinedIcon />}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <InputField
            label="Nombre"
            name="firstName"
            errors={hook.errors}
            register={hook.register}
            sxInput={{ mt: 0 }}
            sxLabel={{ color: theme.palette.secondary.main, fontSize: '1rem' }}
            icon={<PersonOutlineOutlinedIcon />}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <InputField
            label="Apellido"
            name="lastName"
            errors={hook.errors}
            register={hook.register}
            sxInput={{ mt: 0 }}
            sxLabel={{ color: theme.palette.secondary.main, fontSize: '1rem' }}
            icon={<PersonOutlineOutlinedIcon />}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <FormControl fullWidth error={Boolean(hook.errors.phone)}>
            <MuiTelInput
              {...hook.register('phone')}
              label="Teléfono"
              defaultCountry={
                hook.userCountry?.toUpperCase() === 'MEX'
                  ? 'MX'
                  : hook.userCountry?.toUpperCase() === 'COL'
                    ? 'CO'
                    : hook.userCountry?.toUpperCase() === 'PER'
                      ? 'PE'
                      : hook.userCountry?.toUpperCase() === 'CHL'
                        ? 'CL'
                        : 'MX'
              }
              onlyCountries={['MX', 'PE', 'CO', 'CL']}
              value={hook.phoneValue}
              onChange={(value: string) =>
                hook.setValue('phone', value.replace(/\s/g, ''), { shouldValidate: true })
              }
              error={Boolean(hook.errors.phone)}
              helperText={hook.errors.phone?.message}
              fullWidth
            />
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle2" color="text.primary" gutterBottom>
            Seguridad
          </Typography>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <InputField
            label="Contraseña"
            name="password"
            register={hook.register}
            errors={hook.errors}
            type={hook.showPassword['password'] ? 'text' : 'password'}
            sxInput={{ mt: 0 }}
            sxLabel={{ color: theme.palette.secondary.main, fontSize: '1rem' }}
            icon={<LockOutlinedIcon />}
            textFieldProps={{
              slotProps: {
                input: {
                  endAdornment: (
                    <IconButton onClick={() => hook.onChgShowPassword('password')}>
                      {hook.showPassword['password'] ? (
                        <VisibilityOffOutlinedIcon />
                      ) : (
                        <RemoveRedEyeOutlinedIcon />
                      )}
                    </IconButton>
                  ),
                },
              },
            }}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <InputField
            label="Confirmar Contraseña"
            name="confirmPassword"
            register={hook.register}
            errors={hook.errors}
            type={hook.showPassword['confirmPassword'] ? 'text' : 'password'}
            sxInput={{ mt: 0 }}
            sxLabel={{ color: theme.palette.secondary.main, fontSize: '1rem' }}
            icon={<LockOutlinedIcon />}
            textFieldProps={{
              slotProps: {
                input: {
                  endAdornment: (
                    <IconButton onClick={() => hook.onChgShowPassword('confirmPassword')}>
                      {hook.showPassword['confirmPassword'] ? (
                        <VisibilityOffOutlinedIcon />
                      ) : (
                        <RemoveRedEyeOutlinedIcon />
                      )}
                    </IconButton>
                  ),
                },
              },
            }}
          />
        </Grid>

        <Grid size={{ xs: 12 }} display="flex" justifyContent="flex-end">
          <Button
            onClick={hook.onSubmit}
            disabled={!hook.isValid || hook.isCreating}
            variant="contained"
            startIcon={hook.isCreating ? <CircularProgress size={20} /> : undefined}
          >
            {hook.isCreating ? 'Creando usuario...' : 'Crear usuario'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NewUsers;
